import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api } from '../../api/client'
import { GoldSummary, type Indicator } from '../data/reports/GoldReportPage'

type Entry = {
  user_id: number
  name: string
  sessions: number
  last_session: string | null
  status: string
  updated_at: string | null
  analytics: { indicators: Indicator[] } | null
}
type Dashboard = { players: Entry[]; note: string }

export function ClubReportDashboard({ clubId }: { clubId: number }) {
  const [data, setData] = useState<Dashboard | null>(null)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState<number | null>(null)
  const [message, setMessage] = useState('')
  const load = useCallback(async () => {
    setError('')
    try {
      setData(await api.get<Dashboard>(`/club/${clubId}/report-dashboard`))
    } catch (e) {
      setError(
        e instanceof Error ? e.message : 'No pudimos cargar los informes.',
      )
    }
  }, [clubId])
  useEffect(() => {
    setData(null)
    void load()
  }, [load])
  async function rebuild(player: Entry) {
    setBusy(player.user_id)
    setError('')
    setMessage('')
    try {
      await api.post(`/club/${clubId}/players/${player.user_id}/report/rebuild`)
      await load()
      setMessage(`Resumen de ${player.name} actualizado.`)
    } catch (e) {
      setError(
        e instanceof Error ? e.message : 'No pudimos actualizar el resumen.',
      )
    } finally {
      setBusy(null)
    }
  }
  const players = [...(data?.players ?? [])].sort((a, b) => {
    const priority = (p: Entry) =>
      p.status==='current' && p.analytics?.indicators.some((i) =>
        ['red', 'priorizar'].includes(i.state),
      )
        ? 0
        : p.status !== 'current'
          ? 1
          : 2
    return priority(a) - priority(b) || a.name.localeCompare(b.name)
  })
  return (
    <section className="space-y-md min-w-0" aria-label="Informes del plantel">
      <h2 className="text-lg font-semibold">Informes del plantel</h2>
      <p className="text-sm text-text-muted">
        Primero los focos de práctica y los resúmenes por actualizar. Cada
        informe incluye cuestionarios, fisiología, decisiones y una guía de
        lectura.
      </p>
      {error && (
        <div role="alert" className="banner-err">
          {error}{' '}
          <button className="btn-ghost" onClick={() => void load()}>
            Reintentar
          </button>
        </div>
      )}
      {message && (
        <p role="status" className="banner-ok">
          {message}
        </p>
      )}
      {!data && !error && (
        <p role="status">Cargando los resúmenes del plantel…</p>
      )}
      {data && players.length === 0 && (
        <p>No hay jugadores en el plantel. Sumalos desde Plantel.</p>
      )}
      {players.map((p) => (
        <article key={p.user_id} className="card min-w-0">
          <div className="flex flex-wrap items-start justify-between gap-md">
            <div className="min-w-0">
              <h3 className="font-semibold break-words">{p.name}</h3>
              <p className="text-sm text-text-muted">
                {p.sessions} sesiones ·{' '}
                {p.last_session
                  ? new Date(p.last_session).toLocaleDateString('es-AR')
                  : 'Sin sesiones'}
              </p>
            </div>
            <div className="flex flex-wrap items-center gap-sm">
              <Link
                className="btn-ghost text-primary"
                to={`/club/${clubId}/players/${p.user_id}/report`}
              >
                Ver informe
              </Link>
              <button
                className="btn-ghost"
                disabled={busy !== null}
                onClick={() => void rebuild(p)}
              >
                {busy === p.user_id ? 'Calculando…' : 'Actualizar resumen'}
              </button>
            </div>
          </div>
          {p.status !== 'current' && (
            <p className="text-sm text-text-muted mt-sm">
              {p.status === 'stale'
                ? 'Hay datos nuevos: actualizá el resumen antes de compararlo.'
                : 'El resumen todavía no fue calculado.'}
            </p>
          )}
          {p.analytics && p.status==='current' && <div className="mt-md"><GoldSummary indicators={p.analytics.indicators} /></div>}
          {p.updated_at && (
            <p className="text-xs text-text-muted mt-sm">
              Calculado: {new Date(p.updated_at).toLocaleString('es-AR')}
            </p>
          )}
        </article>
      ))}
      {data && (
        <ClubGroup players={players.filter((p) => p.status === 'current')} />
      )}
    </section>
  )
}

function ClubGroup({ players }: { players: Entry[] }) {
  const groups = new Map<
    string,
    { title: string; values: number[]; unit: string }
  >()
  players.forEach((p) =>
    p.analytics?.indicators.forEach((i) => {
      if (
        i.value === null ||
        !Number.isFinite(i.value) ||
        !i.stratum ||
        Object.values(i.stratum).some((v) => v == null || v === 'unknown')
      )
        return
      const key = `${i.id}:${JSON.stringify(Object.entries(i.stratum).sort())}`
      const group = groups.get(key) ?? {
        title: i.title,
        values: [],
        unit: i.unit,
      }
      group.values.push(i.value)
      groups.set(key, group)
    }),
  )
  const eligible = [...groups.entries()].filter(
    // Each current physiological card represents a single latest session.
    ([, g]) => g.values.length >= 10,
  )
  return (
    <section className="card">
      <h3 className="font-semibold">El club en conjunto</h3>
      {!eligible.length ? (
        <p className="text-sm text-text-muted">
          Referencia del club en construcción. Se requieren al menos 5 jugadores
          y 10 sesiones comparables. Esta vista usa una última sesión por
          jugador, por lo que necesita 10 resúmenes compatibles.
        </p>
      ) : (
        eligible.map(([key, g]) => {
          const v = [...g.values].sort((a, b) => a - b)
          const quantile = (p: number) => v[Math.floor((v.length - 1) * p)] ?? 0
          return (
            <p key={key} className="text-sm">
              {g.title}: mediana{' '}
              {quantile(0.5).toLocaleString('es-AR', {
                maximumFractionDigits: 2,
              })}{' '}
              {g.unit} · mitad central {quantile(0.25).toFixed(2)}–
              {quantile(0.75).toFixed(2)} · n = {v.length} jugadores.
            </p>
          )
        })
      )}
    </section>
  )
}
